"use client"

import { useState, useEffect, type MouseEvent } from "react"
import Image from "next/image"
import { motion, useReducedMotion } from "framer-motion"
import { Images } from "lucide-react"
import { Icon } from "@/shared/components/icons"

import type { GalleryPost } from "@/shared/lib/supabase/mappers"
import { useToggleGalleryLike } from "@/features/gallery/hooks/mutations/use-gallery-mutations"
import { isExternalGalleryPostId } from "@/features/gallery/hooks/queries/use-gallery"
import { ImageFallback } from "@/shared/components/ui/image-fallback"
import { track } from "@/shared/lib/analytics"
import { cn } from "@/shared/lib/utils"
import { useAuthStore } from "@/features/auth/store"
import { useUIStore } from "@/shared/stores/ui-store"

interface GalleryCardProps {
  post: GalleryPost
  onClick?: () => void
  priority?: boolean
}

const aspectRatioMap: Record<string, string> = {
  square: "aspect-square",
  portrait: "aspect-[3/4]",
  landscape: "aspect-[4/3]",
}

function formatCount(count: number): string {
  if (count >= 1000) return `${(count / 1000).toFixed(1)}k`
  return count.toString()
}

export function GalleryCard({ post, onClick, priority = false }: GalleryCardProps) {
  const prefersReducedMotion = useReducedMotion()
  const [isHovered, setIsHovered] = useState(false)
  const [isLiked, setIsLiked] = useState(post.isLiked ?? false)
  const [imgLoaded, setImgLoaded] = useState(false)
  const [showPreviewImage, setShowPreviewImage] = useState(true)
  const [imgError, setImgError] = useState(false)
  const [currentImageUrl, setCurrentImageUrl] = useState(post.mediaUrl)
  const isExternalPost = isExternalGalleryPostId(post.id)
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const tosAccepted = useAuthStore((state) => state.profile?.tosAccepted)
  const setOnboardingRequested = useUIStore((state) => state.setOnboardingRequested)
  const setActiveModal = useUIStore((state) => state.setActiveModal)
  const setAuthContext = useUIStore((state) => state.setAuthContext)
  const previewUrl = post.thumbnailUrl?.trim() ? post.thumbnailUrl : null

  // Reset image loading state when the image URL changes (e.g. new post data)
  useEffect(() => {
    setImgLoaded(false)
    setShowPreviewImage(true)
    setImgError(false)
    setCurrentImageUrl(post.mediaUrl)
  }, [post.mediaUrl])

  // Keep isLiked in sync when prop updates (e.g. after liking in modal)
  useEffect(() => {
    setIsLiked(post.isLiked ?? false)
  }, [post.isLiked])
  useEffect(() => {
    if (!imgLoaded) return

    const timeout = window.setTimeout(
      () => setShowPreviewImage(false),
      prefersReducedMotion ? 150 : 700
    )
    return () => window.clearTimeout(timeout)
  }, [imgLoaded, prefersReducedMotion])
  const toggleLike = useToggleGalleryLike()

  const hasValidImage = !!currentImageUrl && currentImageUrl.length > 0 && !imgError
  const hasPreview = !!previewUrl
  const showPreviewPlaceholder =
    hasPreview && previewUrl !== currentImageUrl && showPreviewImage && !imgError
  const displayedLikes = isLiked ? post.likes + 1 : post.likes
  const batchCount = post.batchCount ?? 1
  const showsBatch = !isExternalPost && batchCount > 1

  const handleLike = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    if (isExternalPost) return
    if (!isAuthenticated) {
      setAuthContext({
        characterId: null,
        characterName: null,
        characterAvatar: null,
        source: "generic",
      })
      setActiveModal("register")
      return
    }
    if (!tosAccepted) {
      setOnboardingRequested(true)
      return
    }
    toggleLike.mutate(post.id, {
      onSuccess: (result) => {
        if (result.requiresOnboarding) {
          setOnboardingRequested(true)
          return
        }
        if (!result.success) return

        const newLiked = result.liked ?? !isLiked
        setIsLiked(newLiked)
        track(newLiked ? "content:like" : "content:unlike", {
          content_type: "gallery",
          content_id: post.id,
        })
      },
    })
  }

  return (
    <motion.div
      className={cn(
        "group relative overflow-hidden bg-card",
        onClick ? "cursor-pointer" : "cursor-default",
        aspectRatioMap[post.aspectRatio] ?? "aspect-square"
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      transition={
        prefersReducedMotion
          ? { duration: 0.15 }
          : { type: "spring", stiffness: 300, damping: 25 }
      }
      onClick={onClick}
    >
      {/* Neutral loading base to avoid random saturated flashes */}
      <div
        className={cn(
          "absolute inset-0 bg-black transition-opacity duration-500",
          imgLoaded ? "opacity-0" : "opacity-100"
        )}
      >
        {!imgLoaded && hasValidImage && !hasPreview && (
          <div
            className={cn(
              "absolute inset-0 bg-gradient-to-r from-transparent via-white/8 to-transparent",
              prefersReducedMotion
                ? "opacity-40"
                : "-translate-x-full animate-[shimmer_2s_infinite]"
            )}
          />
        )}
      </div>

      {/* Tiny preview image while full image is loading */}
      {hasValidImage && showPreviewPlaceholder && (
        <Image
          src={previewUrl}
          alt=""
          fill
          aria-hidden="true"
          unoptimized={isExternalPost}
          referrerPolicy="no-referrer"
          className={cn(
            "scale-110 object-cover blur-3xl transition-opacity ease-out",
            prefersReducedMotion ? "duration-150" : "duration-700",
            imgLoaded ? "opacity-0" : "opacity-100"
          )}
          sizes="64px"
          quality={20}
        />
      )}

      {/* Full quality image */}
      {hasValidImage && (
        <Image
          src={currentImageUrl}
          alt={post.caption || "Gallery image"}
          fill
          unoptimized={isExternalPost}
          referrerPolicy="no-referrer"
          className={cn(
            "object-cover transition-opacity",
            prefersReducedMotion ? "duration-200" : "duration-700",
            imgLoaded ? "opacity-100" : "opacity-0"
          )}
          sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
          priority={priority}
          onLoad={() => setImgLoaded(true)}
          onError={() => {
            if (previewUrl && currentImageUrl !== previewUrl) {
              setCurrentImageUrl(previewUrl)
              return
            }
            setImgError(true)
          }}
        />
      )}

      {/* Fallback when no image or image failed */}
      {!hasValidImage && (
        <ImageFallback className="absolute inset-0" />
      )}

      {showsBatch && (
        <div
          className="pointer-events-none absolute left-2 top-2 z-20 inline-flex items-center gap-1 rounded-full border border-white/15 bg-black/42 px-2 py-1 text-[11px] font-medium text-white/85 backdrop-blur-md"
          aria-label={`${batchCount} images in this set`}
        >
          <Images className="size-3.5" />
          <span>{batchCount}</span>
        </div>
      )}

      {/* Heart overlay on hover */}
      <motion.div
        className="pointer-events-none absolute right-2 top-2 z-20 hidden md:block"
        initial={{ opacity: 0 }}
        animate={{ opacity: isHovered ? 1 : 0 }}
        transition={{ duration: prefersReducedMotion ? 0.1 : 0.2 }}
      >
        <motion.button
          className="pointer-events-auto rounded-full border border-white/20 bg-black/35 p-2.5 text-white backdrop-blur-md transition-colors hover:bg-black/55 disabled:cursor-default"
          whileTap={prefersReducedMotion ? undefined : { scale: 0.85 }}
          onClick={handleLike}
          disabled={isExternalPost}
          aria-label={isLiked ? "Unlike post" : "Like post"}
        >
          <Icon
            name="heart"
            variant={isLiked ? "bold" : "outline"}
            size={18}
            className={cn(
              "size-[18px] transition-colors",
              isLiked ? "text-red-500" : "text-white"
            )}
          />
        </motion.button>
      </motion.div>

      {/* Touch-first quick like button */}
      <button
        type="button"
        className="absolute right-2 top-2 z-20 inline-flex rounded-full border border-white/20 bg-black/45 p-2 text-white backdrop-blur-sm transition-colors hover:bg-black/60 disabled:cursor-default md:hidden"
        onClick={handleLike}
        disabled={isExternalPost}
        aria-label={isLiked ? "Unlike post" : "Like post"}
      >
        <Icon
          name="heart"
          variant={isLiked ? "bold" : "outline"}
          size={16}
          className={cn(
            "h-4 w-4 transition-colors",
            isLiked ? "text-red-500" : "text-white"
          )}
        />
      </button>

      {displayedLikes > 0 && (
        <div className="pointer-events-none absolute bottom-2 right-2 z-10 hidden rounded-full bg-black/35 px-2 py-1 text-[11px] font-medium text-white/80 backdrop-blur-md md:block">
          {formatCount(displayedLikes)}
        </div>
      )}
    </motion.div>
  )
}
